import {
  Alert,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useAccountsList } from '@/models/accounts';
import { type Lead, useQualifyLead } from '@/models/leads';

export type QualifyPerson = {
  qualified: boolean;
  accountId?: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  telegram: string | null;
};

const displayName = (first: string, last: string) => {
  const f = first.split(/\s+/).filter((w) => w && !w.includes('@'));
  const l = last.split(/\s+/).filter((w) => w && !w.includes('@'));
  const tail = f.slice(Math.max(0, f.length - l.length)).join(' ');
  const words =
    l.length && tail.toLowerCase() === l.join(' ').toLowerCase()
      ? f
      : [...f, ...l];
  return words
    .filter((w, i) => i === 0 || w.toLowerCase() !== words[i - 1].toLowerCase())
    .join(' ');
};

export function QualifyLeadModal({
  lead,
  person,
  opened,
  onClose,
}: {
  lead: Lead;
  person?: QualifyPerson | null;
  opened: boolean;
  onClose: () => void;
}) {
  const qualify = useQualifyLead();
  const isClient = person
    ? person.qualified && person.accountId != null
    : lead.contactId != null;
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const accounts = useAccountsList({
    search: debounced,
    pageSize: 20,
    sort: 'name',
    order: 'asc',
  });
  const form = useForm({
    initialValues: {
      firstName: person?.firstName ?? lead.firstName ?? '',
      lastName: person?.lastName ?? lead.lastName ?? '',
      email: person?.email ?? lead.email ?? '',
      phone: person?.phone ?? lead.phone ?? '',
      whatsapp: person?.whatsapp ?? lead.whatsapp ?? '',
      telegram: person?.telegram ?? lead.telegram ?? '',
      notes: lead.extendedMessage ?? '',
    },
  });

  const derivedAccountName =
    displayName(form.values.firstName, form.values.lastName) ||
    form.values.phone ||
    'Unnamed account';

  const submit = form.onSubmit(async (values) => {
    if (!isClient && !values.email && !values.phone) {
      form.setFieldError('email', 'email or phone required');
      form.setFieldError('phone', 'email or phone required');
      return;
    }
    if (!isClient && mode === 'existing' && !accountId) {
      notifications.show({ color: 'red', message: 'Choose an account' });
      return;
    }
    try {
      await qualify.mutateAsync({
        leadId: lead.id,
        payload: isClient ? undefined : values,
        accountId:
          !isClient && mode === 'existing' && accountId
            ? Number(accountId)
            : null,
      });
      notifications.show({ color: 'green', message: 'Request qualified' });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not qualify',
        message: String(e),
      });
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Qualify lead" size="lg">
      <form onSubmit={submit}>
        <Stack>
          <Alert color="blue" variant="light">
            <Text size="sm">
              SQL becomes Qualified. The lead keeps its stage and stays
              editable.
            </Text>
            {isClient ? (
              <Text size="sm" mt={6}>
                This person is already qualified and has an account; the request
                is qualified on that person.
              </Text>
            ) : mode === 'new' ? (
              <Text size="sm" mt={6}>
                This person becomes qualified with a new account named{' '}
                <b>{derivedAccountName}</b>, using the details below.
              </Text>
            ) : (
              <Text size="sm" mt={6}>
                This person becomes qualified in the account you choose.
              </Text>
            )}
          </Alert>
          {!isClient && (
            <>
              <SegmentedControl
                value={mode}
                onChange={(v) => setMode(v as 'new' | 'existing')}
                data={[
                  { value: 'new', label: 'New account' },
                  { value: 'existing', label: 'Existing account' },
                ]}
              />
              {mode === 'existing' && (
                <Select
                  label="Account"
                  placeholder="Search accounts"
                  searchable
                  clearable
                  searchValue={search}
                  onSearchChange={setSearch}
                  value={accountId}
                  onChange={setAccountId}
                  nothingFoundMessage="No account you can see"
                  data={(accounts.data?.rows ?? []).map((a) => ({
                    value: String(a.id),
                    label: a.name,
                  }))}
                />
              )}
              <Group grow>
                <TextInput
                  label="First name"
                  {...form.getInputProps('firstName')}
                />
                <TextInput
                  label="Last name"
                  {...form.getInputProps('lastName')}
                />
              </Group>
              <Group grow>
                <TextInput
                  label="Email"
                  type="email"
                  {...form.getInputProps('email')}
                />
                <TextInput label="Phone" {...form.getInputProps('phone')} />
              </Group>
              <Group grow>
                <TextInput
                  label="WhatsApp"
                  {...form.getInputProps('whatsapp')}
                />
                <TextInput
                  label="Telegram"
                  {...form.getInputProps('telegram')}
                />
              </Group>
              <Textarea
                label="Notes (optional, on the person)"
                autosize
                minRows={2}
                {...form.getInputProps('notes')}
              />
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={qualify.isPending}>
              Qualify
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
